import React, { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AuthShell } from '../../components/AuthShell/AuthShell';
import { Button } from '../../components/ui/Button/Button';
import { EyeIcon, EyeOffIcon } from '../../assets/icons/icons';
import { useChangeLoginPasswordMutation } from '../../entities/session/api/sessionApi';
import { cx } from '../../utils/cx';
import styles from './NewPasswordPage.module.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zа-я]{2,}$/i;

interface Rule {
    label: string;
    test: (value: string) => boolean;
}

/**
 * Требования показываем списком, а не по одному сообщению:
 * иначе пользователь исправляет пароль вслепую, по одной ошибке за раз.
 */
const RULES: Rule[] = [
    { label: 'Не короче 10 символов', test: (v) => v.length >= 10 },
    { label: 'Есть заглавная буква', test: (v) => /[A-ZА-Я]/.test(v) },
    { label: 'Есть строчная буква', test: (v) => /[a-zа-я]/.test(v) },
    { label: 'Есть цифра', test: (v) => /\d/.test(v) },
    { label: 'Без пробелов', test: (v) => v.length > 0 && !/\s/.test(v) },
];

export const NewPasswordPage: FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [repeat, setRepeat] = useState('');
    const [email, setEmail] = useState('');
    const [touched, setTouched] = useState(false);

    /* У каждого поля свой глазик: показать одно, не раскрывая второе. */
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [repeatVisible, setRepeatVisible] = useState(false);

    const [changeLoginPassword, { isLoading }] = useChangeLoginPasswordMutation();

    const passed = useMemo(() => RULES.map((rule) => rule.test(password)), [password]);
    const isStrong = passed.every(Boolean);
    const isMatching = repeat.length > 0 && repeat === password;
    const isEmailValid = EMAIL_RE.test(email.trim());

    const canSubmit = isStrong && isMatching && isEmailValid && !isLoading;

    const submit = useCallback(async (): Promise<void> => {
        setTouched(true);
        if (!canSubmit || !token) return;

        try {
            await changeLoginPassword({
                token,
                body: {
                    email: email.trim(),
                    password,
                    repeat_password: repeat,
                },
            }).unwrap();

            navigate('/password-changed', { replace: true });
        } catch {
            /* тост об ошибке уже показал notifyOnError */
        }
    }, [canSubmit, token, email, password, repeat, changeLoginPassword, navigate]);

    return (
        <AuthShell
            title="Новый пароль"
            lead="Придумайте пароль, который не используете в других сервисах."
            footer={
                <Link className={styles.back} to="/login">
                    ← Вернуться ко входу
                </Link>
            }
        >
            <div className={styles.field}>
                <label className={styles.label} htmlFor="email">
                    Электронная почта
                </label>

                <input
                    id="email"
                    type="email"
                    className={cx(styles.input, touched && !isEmailValid && styles.inputError)}
                    value={email}
                    placeholder="Та же, на которую пришло письмо"
                    autoComplete="email"
                    onChange={(event) => setEmail(event.target.value)}
                />
            </div>

            <div className={styles.field}>
                <label className={styles.label} htmlFor="password">
                    Пароль
                </label>

                <span className={styles.passwordField}>
                    <input
                        id="password"
                        type={passwordVisible ? 'text' : 'password'}
                        className={cx(styles.input, styles.passwordInput)}
                        value={password}
                        placeholder="Не менее 10 символов"
                        autoComplete="new-password"
                        onChange={(event) => setPassword(event.target.value)}
                    />
                    <button
                        type="button"
                        className={styles.eye}
                        onClick={() => setPasswordVisible((visible) => !visible)}
                        aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                        {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                </span>

                <ul className={styles.rules}>
                    {RULES.map((rule, index) => (
                        <li
                            key={rule.label}
                            className={cx(styles.rule, passed[index] && styles.ruleOk)}
                        >
                            {passed[index] ? '✓' : '•'} {rule.label}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={styles.field}>
                <label className={styles.label} htmlFor="repeat">
                    Повторите пароль
                </label>

                <span className={styles.passwordField}>
                    <input
                        id="repeat"
                        type={repeatVisible ? 'text' : 'password'}
                        className={cx(
                            styles.input,
                            styles.passwordInput,
                            repeat.length > 0 && !isMatching && styles.inputError,
                        )}
                        value={repeat}
                        placeholder="Повторите пароль"
                        autoComplete="new-password"
                        onChange={(event) => setRepeat(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') void submit();
                        }}
                    />
                    <button
                        type="button"
                        className={styles.eye}
                        onClick={() => setRepeatVisible((visible) => !visible)}
                        aria-label={repeatVisible ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                        {repeatVisible ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                </span>

                {repeat.length > 0 && !isMatching && (
                    <span className={styles.error}>Пароли не совпадают</span>
                )}
            </div>

            <Button variant="primary" disabled={!canSubmit} onClick={submit}>
                {isLoading ? 'Сохраняем...' : 'Сохранить пароль'}
            </Button>
        </AuthShell>
    );
};